import BoxMath from "./boxMath";
import DockableBox from "./dockableBox";
import DockingCalculator from "./dockingCalculator";
import { System } from "../../../../common/system";
import { checkIfBoundsAreEqual, wrapWithTimeout, isWindows } from "../../../../common/util";
import StorageClient from "../../../../clients/storageClient";
import { clone } from "../../../../common/util";
import { MOVE_REASON, WINDOWSTATE } from "../../../../common/constants";
import FinsembleEvent from "../../../../common/events/FinsembleEvent";
import { adjustBoundsToBeOnMonitorAsync } from "../../../../common/Monitors";
import { RouterClient } from "../../../../clients/routerClient";
import { DockableMonitor } from "./dockableMonitor";
import { MonitorInfoDetail } from "../../../../FEA";
import { StandardCallback, StandardPromise } from "../../../../types";
import { FSBLWindow } from "../localTypes";
import { Logger } from "../../../../clients/logger";
import {
	DockingConfigOptions,
	DefaultDockingOptions,
	defaultDockingOptions,
	DockedDimensions,
	DockingBounds,
	Vertex,
	MonitorDockablePositions,
	BoxCoordinates,
	MoveRequest,
	DockingSimulatedEvent,
	SnappedObject,
	SharedEdges,
} from "./types";
import pLimit from "p-limit";
import uuidv4 from "uuid-random";

type WindowBounds = {
	dockedPosition?: number;
	monitor: string;
	top: number;
	bottom: number;
	left: number;
	right: number;
	width: number;
	height: number;
};

type DockedLocation = {
	visible: boolean;
	"window-bounds": WindowBounds;
	monitorData: MonitorInfoDetail[];
	dockedPositionBeforeHidden?: number | null;
};

function NullStandardCallback(err) {
	err;
}

export class DockableWindow extends DockableBox {
	win: FSBLWindow;

	nativeEdgeTolerance: number;

	canGroup: boolean;

	name: string;

	guid: string;

	hasDashbarData: boolean;

	opacity: number;

	groupNames: string[];

	snappedWindows: SnappedObject[] = [];

	calculator: DockingCalculator;

	resizeThrottlePeriod: number = 5;

	changeTaskbarVisibilityQueue: Function;

	lastBoundsAdjustment: number = Date.now();

	animate: Function;

	// Needed for dockable components that don't have a static grabber on the side of the component (mostly for when there are dockable windows). When a user undocks the component, they can undock it from anywhere in the components header bar, this will center the window on the undock, so that when the window shrinks back to its old size, the mouse will be in the center
	shouldOffsetByMouse: boolean = false;

	allowTiling: boolean;

	allowTabbing: boolean;

	canMinimize: boolean;

	shouldShowTaskbarIcon: boolean;

	isDockableComponent: boolean = false;

	monitorDockablePositions: MonitorDockablePositions = {};

	dockingOptions: DockingConfigOptions;

	dockedPosition: number | undefined;

	ignoreSnappingRequests: boolean = false;

	dockedDimensions: DockedDimensions | null = null;

	undockedPosition!: DockedDimensions;

	primaryMonitor?: MonitorInfoDetail;

	isHidden: boolean = false;

	dockedParams!: DockingConfigOptions;

	dockedHeight!: number;

	snappedEdge!: string;

	dockedMonitor?: DockableMonitor;

	snappedMonitor?: DockableMonitor;

	monitor: DockableMonitor;

	onboundschanged: number = 0;

	previouslySnappedEdge?: boolean;

	dockedBounds?: DockingBounds;

	justDocked?: boolean;

	vertices!: Record<string, Vertex>;

	groups!: string[];

	closeEventListener?: Function;

	finished!: boolean;

	limits: any;

	edges: any;

	onGroupEdge: any;

	justUndocked!: boolean;

	dockedPositionBeforeHidden: number | null = null;

	/**
	 * Creates DockableWindow instances.
	 */
	static factory(win: FSBLWindow, bounds: BoxCoordinates, calculator: DockingCalculator): DockableWindow {
		const dockableWindow = new DockableWindow(win, bounds, calculator);
		dockableWindow.initialize();
		return dockableWindow;
	}

	/**
	 * Do not construct DockableWindow directly. Use the factory
	 *
	 * @param {*} win Window object; WebWindow, ExternalWindow, etc.
	 * @param {bounds} bounds initial bounds for the window.
	 * @param {object} dependencies Dependency object that provides the calculator, System, and Logger.
	 * @param {DockingCalculator} dependencies.calculator
	 * @param {System} dependencies.System
	 * @param {Logger} dependencies.Logger
	 */
	private constructor(win: FSBLWindow, bounds: BoxCoordinates, calculator: DockingCalculator) {
		super(bounds);
		this.name = win.name ?? "undefined";
		Logger.system.debug("dockableWindow constructing", this.name);

		this.calculator = calculator;
		this.win = win;
		this.nativeEdgeTolerance = calculator.nativeWindowEdgeTolerance;
		this.bufferSize = 15;
		this.guid = uuidv4();
		this.top = bounds.top;
		this.left = bounds.left;
		this.right = bounds.right;
		this.bottom = bounds.bottom;
		this.width = bounds.width || 0;
		this.height = bounds.height || 0;
		this.hasDashbarData = false;
		this.opacity = 1;
		this.groupNames = [];
		this.buildDefaultDockingOptions = this.buildDefaultDockingOptions.bind(this);
		this.onBoundsChanged = this.onBoundsChanged.bind(this);
		this.onBoundsChanging = this.onBoundsChanging.bind(this);
		this.requestBoundsChange = this.requestBoundsChange.bind(this);
		this.getDockedStringFromLocation = this.getDockedStringFromLocation.bind(this);
		this.hiddenEventHandler = this.hiddenEventHandler.bind(this);
		this.shownEventHandler = this.shownEventHandler.bind(this);
		this.onSystemMovedWindow = this.onSystemMovedWindow.bind(this);
		this.restored = this.restored.bind(this);
		this.maximized = this.maximized.bind(this);
		this.dock = this.dock.bind(this);
		this.undock = this.undock.bind(this);
		this.animate = this.animatePositionAndHeight;
		// The default is to allow grouping. But if it is disabled using any of the various methods, disable it.
		this.canGroup =
			win.canGroup ??
			win?.windowDescriptor?.customData?.window?.canGroup ??
			win?.windowDescriptor?.customData?.foreign?.services?.dockingService?.canGroup ??
			win?.windowDescriptor?.customData?.foreign?.services?.windowService?.allowGrouping ??
			win?.windowDescriptor?.customData?.foreign?.services?.windowService?.manageWindowMovement ??
			true;

		this.monitor = this.calculator.getMonitorForWindow(this);

		this.disableFrame();

		// Changing the visibility of the taskbar icon is a long process. It involves
		// many asynchronous calls. In order to make sure a call sequence of
		// hide, show, show, hide results in a hidden taskbar icon, we put show and hide on a queue.
		this.changeTaskbarVisibilityQueue = pLimit(1);

		// A DockableWindow can refuse tabbing or tiling requests with the right config props
		this.allowTiling = win.tiling ?? true;
		this.allowTabbing = win.tabbing ?? false;
		this.canMinimize = win.minimize !== undefined;
		// If the windowOptions say to show the taskbar icon, respect that. Default to true.
		// This value is checked inside of showTaskbarIcon()
		this.shouldShowTaskbarIcon = win.windowOptions?.showTaskbarIcon ?? true;

		this.dockingOptions = win.dockingOptions;

		// disable grouping and tabbing for windows that are not in the workspace
		if (
			(win?.windowDescriptor as any)?.addToWorkspace === false &&
			win?.windowDescriptor?.windowInterop?.parent?.windowName !== "workspaceService"
		) {
			this.canGroup = false;
			this.allowTabbing = false;
			this.calculator.addToGroupBlacklist(this.name);
		}
	}

	private setMonitorDockablePosition(options: any) {
		const { dockable }: { dockable: any[] } = options;
		if (Array.isArray(dockable)) {
			dockable.forEach((option) => {
				option = option.toUpperCase();

				if (option === "TOP") this.monitorDockablePositions.TOP = 0;
				if (option === "LEFT") this.monitorDockablePositions.LEFT = 1;
				if (option === "RIGHT") this.monitorDockablePositions.RIGHT = 2;
				if (option === "BOTTOM") this.monitorDockablePositions.BOTTOM = 3;
			});
			this.monitorDockablePositions.NONE = 4;
		} else {
			// If options.dockable is not supplied, or it is just 'true' that means this window can dock everywhere
			this.monitorDockablePositions = {
				TOP: 0,
				LEFT: 1,
				RIGHT: 2,
				BOTTOM: 3,
				NONE: 4,
			};
		}
	}

	private computeDockingDefaults(options: any): DefaultDockingOptions {
		const spawnData = this.win?.windowDescriptor?.customData?.spawnData ?? null;
		const dockingDefaults: DefaultDockingOptions =
			options !== null ? this.buildDefaultDockingOptions(options) : defaultDockingOptions;

		this.isDockableComponent = dockingDefaults.isDockable;
		this.hasDashbarData = (spawnData?.dashbar?.height && !Number.isNaN(spawnData.dashbar.height)) ?? false;

		const isUndocked = this.win.dockingOptions?.dockable && this.win.dockingOptions.undockedParams !== undefined;
		const isDocked = this.win.dockingOptions?.dockable && this.win.dockingOptions?.dockedParams !== undefined;

		if (isUndocked) {
			options.undockedParams = {
				width: dockingDefaults.undockedWidth,
				height: dockingDefaults.undockedHeight,
			};
			if (this.hasDashbarData) options.undockedParams.height = options.undockedParams.height + spawnData.dashbar.height;
			options.maxHeight = options.undockedParams.height;
		}

		if (isDocked) {
			dockingDefaults.dockedHeight = this.win.dockingOptions.dockedParams.height;
			if (this.hasDashbarData) dockingDefaults.dockedHeight = dockingDefaults.dockedHeight + spawnData.dashbar.height;
			options.dockedParams = {
				width: dockingDefaults.dockedWidth,
				height: dockingDefaults.dockedHeight,
			};
		}

		if (this.isDockableComponent) this.setMonitorDockablePosition(options);
		// pass through from the windowWrap. This property is added during the registration. Options include, min/max height/width, whether the window is dockable, whether it starts docked, etc.
		this.dockedPosition = this.monitorDockablePositions.NONE;
		this.dockedDimensions = {
			width: dockingDefaults.dockedWidth,
			height: dockingDefaults.dockedHeight,
		};
		return dockingDefaults;
	}

	// options is pass-by-reference
	private setMinMaxWidths(options: any) {
		const { minWidth = 0, minHeight = 0, maxWidth = 0, maxHeight = 0 } = this.win.windowDescriptor;
		// Checking for greater than 0 since -1 and 0 both disable the bound containment.
		if (minWidth > 0) options.minWidth = minWidth;
		if (minHeight > 0) options.minHeight = minHeight;
		if (maxWidth > 0) options.maxWidth = maxWidth;
		if (maxHeight > 0) options.maxHeight = maxHeight;
	}

	private async initialize() {
		// This is triggered on a window move which is done by the user dragging a held down mouse over a window. In other words, this is only !finishedMove when a user is moving the window
		// DH 3/6/2019 Setting the "markDirty" param to false ensures instantiating
		// the DockableWindow doesn't mistakenly mark the workspace dirty.
		this.win._stopMove();
		this.setBoundingBoxes();
		this.addListeners();

		let options = this.win.dockingOptions ?? null;
		this.ignoreSnappingRequests = options?.ignoreSnappingRequests || options?.ephemeral;
		this.shouldShowTaskbarIcon = options?.showTaskbarIcon ?? true;

		// this should work only for native and WPF apps
		if (options && this.win.windowType === "NativeWindow" && this.win.windowDescriptor) this.setMinMaxWidths(options);

		const dockingDefaults = this.computeDockingDefaults(options);

		// If overwriteDockedOptions is true we should expect there to be some information regarding this component in memory. That saved information will provide bounds for where the window should spawn
		const startsDocked = typeof options?.startsDocked === "string" ? options.startsDocked.toUpperCase() : "NONE";

		if (this.isDockableComponent && options?.overwriteDockedOptions) {
			const locValue = await this.recallDockedPosition({ startsDocked, dockingDefaults });
			options.locValue = locValue;
			this.undockedPosition = options.undockedParams;
			this.dockedParams = options.dockedParams;
		} else if (dockingDefaults.isDocked && this.monitorDockablePositions.hasOwnProperty(startsDocked)) {
			this.dockedPosition = this.monitorDockablePositions[startsDocked];
			this.undockedPosition = {
				width: dockingDefaults.undockedWidth,
				height: dockingDefaults.undockedHeight,
			};
		}

		this.win.isDocked = this.dockedPosition !== this.monitorDockablePositions.NONE;

		// If the registering window is dockable, and wants to 'start docked' we need to determine which of the dockable positions it requests to start docked in. If the position is found, we call to dock it which will set the appropriate variables and move any launched windows out of its space.
		if (!this.isHidden && this.win.isDocked) {
			// We only dock the window if it's visible. If it's hidden, we don't want to claim space.
			const locValue = options.locValue ?? this.getDockedStringFromLocation();
			if (locValue) await this.dockToMonitor(locValue);
		}

		if (this.isDockableComponent && options?.overwriteDockedOptions) {
			// now that window is up, make sure the monitor data is saved (also saved when bounds change or monitors change)
			this.saveDockableData();
		}
	}

	private async dockToMonitor(locValue: string) {
		const monitor = this.calculator.getMonitorForWindow(this);
		const windowBounds = {
			top: this.top,
			bottom: this.bottom,
			left: this.left,
			right: this.right,
			width: this.width,
			height: this.height,
		};

		const { err } = await this.setBounds(windowBounds);
		if (err) {
			Logger.system.error("Error moving dockable component to default docked position");
		}
		this.dockedHeight = this.dockedParams.height ?? 0;
		this.snappedEdge = locValue;
		if (this.win.windowOptions.nativeWindowHandle) {
			/*
				JC 6/24/20: Initial docking needs to be delayed to ensure assimilation has
				started up and is ready to receive requests. I've seen this fail many times
				because assimilation wasn't ready
			*/
			const delayDock = setTimeout(() => {
				clearTimeout(delayDock);
				monitor.dockWindowToMonitor(this, this, this.undockedPosition.width, this.undockedPosition.height, true);
			}, 1500);
		} else {
			Logger.system.error("Failed to initialize a docked window. Cannot find native window handle");
		}
	}

	private async recallBounds(): Promise<DockedLocation | null> {
		// first look in component state for cached toolbar location -- this is only for backwards compatibility with versions before 3.9
		let { err, data: result } = await this.win.getComponentState({});
		Logger.system.debug("MONITOR: getComponentState", err, result);

		const noBoundsInComponentState =
			err || !result || !result.hasOwnProperty("window-bounds") || result["window-bounds"] == null;

		// for backwards compatibility check if bounds data in component state, if not then check in storage
		if (noBoundsInComponentState) {
			Logger.system.debug("MONITOR: read monitor-docked data from storage", err, result);

			// get data from storage -- this is the normal path
			const { err: err2, data } = await StorageClient.getStandardized({
				topic: this.name,
				key: this.name,
			});
			if (err2) Logger.system.warn("WARNING: Attempt to retrieve toolbar storage failed.", err2);
			else result = data;
		} else {
			Logger.system.debug("MONITOR: using monitor-docked data from component state", err, result);
			// since the bounds data is in component state only for backwards compatibility, use it this time then remove it so that going forward the latest values will be picked up from storage.
			this.win.setComponentState({
				field: "window-bounds",
				value: null,
			});
		}
		Logger.system.debug("MONITOR: DockableWindow get results", err, result);
		return result ?? null;
	}

	// windowBounds is pass by reference
	private getLocValue(windowBounds: WindowBounds): string | null {
		Logger.system.debug("MONITOR: changed");
		const serviceMonitors = this.calculator.getMonitors();
		let monitorToDockTo;
		Object.keys(serviceMonitors).forEach((monKey) => {
			const mon = serviceMonitors[monKey];
			if (mon.rawMonitor.deviceId && mon.rawMonitor.deviceId === windowBounds?.monitor) {
				monitorToDockTo = mon.rawMonitor.availableRect;
			}
		});

		if (!monitorToDockTo) {
			monitorToDockTo = this.primaryMonitor?.availableRect;
		}

		if (windowBounds.width >= monitorToDockTo.width) {
			this.width = monitorToDockTo.width;
		}
		if (windowBounds.height >= monitorToDockTo.height) {
			this.height = monitorToDockTo.height;
		}

		const locValue = this.getDockedStringFromLocation();

		Logger.system.debug("MONITOR: changed switch", windowBounds);

		// Depending on the docked location of the window, we need to reset its
		// bounds according to the monitor's new bounds
		switch (locValue) {
			case "top":
				windowBounds.top = monitorToDockTo.top;
				windowBounds.bottom = windowBounds.top + windowBounds.height;
				break;
			case "bottom":
				windowBounds.bottom = monitorToDockTo.bottom;
				windowBounds.top = windowBounds.bottom - windowBounds.height;
				break;
			case "left":
				windowBounds.left = monitorToDockTo.left;
				windowBounds.right = windowBounds.left + windowBounds.width;
				break;
			case "right":
				windowBounds.right = monitorToDockTo.right;
				windowBounds.left = windowBounds.right - windowBounds.width;
				break;
			default:
				Logger.system.error("Initializing docked window docked to an unknown position");
				break;
		}
		Logger.system.debug("MONITOR: changed after switch", windowBounds);

		windowBounds.left = windowBounds.left <= monitorToDockTo.left ? monitorToDockTo.left : windowBounds.left;
		windowBounds.right = windowBounds.right >= monitorToDockTo.right ? monitorToDockTo.right : windowBounds.right;

		delete this.primaryMonitor;
		return locValue;
	}

	private async recallDockedPosition({
		startsDocked,
		dockingDefaults,
	}: {
		startsDocked: string;
		dockingDefaults: DefaultDockingOptions;
	}): Promise<string | null> {
		const dockedLocation: DockedLocation | null = await this.recallBounds();
		const visible = this.win.windowDescriptor?.customData?.window?.options?.autoShow ?? true;
		this.isHidden = !visible;
		const windowBounds = dockedLocation?.["window-bounds"] !== null ? dockedLocation?.["window-bounds"] : null;
		const savedMonitorData = dockedLocation?.monitorData ?? null;

		// If all the saved data is available, then check if monitor changed since last restart;
		// else since all the data isn't available, assume the monitors did not change since last restart;
		const savedDataAvailable = windowBounds && savedMonitorData;
		const monitorChanged = savedDataAvailable ? await this.monitorChanged(savedMonitorData) : false;

		// if could not find bounds data either in window state or in storage, then use default settings.
		if (!windowBounds) {
			Logger.system.debug("MONITOR: DockableWindow using defaults.");

			if (this.monitorDockablePositions[startsDocked])
				this.dockedPosition = this.monitorDockablePositions[startsDocked];
			else this.dockedPosition = this.monitorDockablePositions.TOP;

			this.undockedPosition = {
				width: dockingDefaults.undockedWidth,
				height: dockingDefaults.undockedHeight,
			};

			if (visible) {
				this.show();
			}
			return null;
		}

		// selects the dockedPositionBeforeHidden as actual docked position in the case where
		// the docked toolbar has been hidden by ctrl+alt+h and Finsemble has been restarted
		// otherwise selects the regular state
		this.dockedPosition = dockedLocation?.dockedPositionBeforeHidden ?? windowBounds.dockedPosition;

		// since have window Bounds, check if in visible space.  If not in visible space then will adjust bounds to be visible
		const boundsToCheck = clone(windowBounds);
		boundsToCheck.isDocked = this.dockedPosition !== this.monitorDockablePositions.NONE;
		Logger.system.debug("MONITOR: DockableWindow status", dockedLocation, monitorChanged, boundsToCheck, windowBounds);

		// If the monitors have changed since this window was last loaded (and docked on the last Finsemble run)
		// determine if that monitor still exists (maybe the taskbar has moved or the resolution was changed).
		// If so, try to put the window back in the same place but remaining on the monitor
		const locValue = monitorChanged ? this.getLocValue(windowBounds) : null;

		this.top = windowBounds.top;
		this.bottom = windowBounds.bottom;
		this.left = windowBounds.left;
		this.right = windowBounds.right;
		this.width = windowBounds.width;
		this.height = windowBounds.height;

		// move undocked toolbar to its previously saved location
		this.setBounds(windowBounds, () => {
			// NOTE: there is a bug related to the "hidden" event that prevents visible state from being saved, so initial visibility will alway be true until bug fixed
			if (visible) {
				this.isHidden = false;
				this.show();
				// observed problem with toolbar not being on top, so bring to front to
				this.win._bringToFront({});
			}
		});
		return locValue;
	}

	/**
	 * Returns turn if the current monitor dimensions are different from the previous monitor dimensions
	 * @param {array} previousMonitorData the previous monitor info (an array of monitors)
	 */
	async monitorChanged(previousMonitorData: MonitorInfoDetail[]): Promise<boolean> {
		const monitorInfo = await System.getMonitorInfo();
		const monitorData = [monitorInfo.primaryMonitor, ...monitorInfo.nonPrimaryMonitors];
		let changed = false;
		// Cache primary monitor for temporary retrieval in docking initialization
		this.primaryMonitor = monitorInfo.primaryMonitor;
		if (monitorData && previousMonitorData && monitorData.length == previousMonitorData.length) {
			changed = monitorData.some((monitor, i) => {
				if (monitor.availableRect.top != previousMonitorData[i].availableRect.top) return true;
				if (monitor.availableRect.bottom != previousMonitorData[i].availableRect.bottom) return true;
				if (monitor.availableRect.left != previousMonitorData[i].availableRect.left) return true;
				return monitor.availableRect.right != previousMonitorData[i].availableRect.right;
			});
		} else {
			changed = true;
		}
		Logger.system.debug(
			"MONITOR: DockableWindow.monitorChanged",
			"monitorData",
			monitorData,
			"previousMonitorData",
			previousMonitorData,
			changed ? "changed" : "not changed"
		);
		return changed;
	}

	/**
	 * Returns turn if the toolbar handle (i.e. what the user can grab to move toolbar) is visible on one of the current monitors (based on the toolbars current bounds
	 * @param {array} currentBounds the toolbars current bounds
	 */
	isToolbarVisible(currentBounds: DockingBounds): Promise<boolean> {
		const isToolbarVisiblePromiseResolver = (resolve) => {
			System.getMonitorInfo((monitorInfo) => {
				const monitorData = [monitorInfo.primaryMonitor, ...monitorInfo.nonPrimaryMonitors];
				let isVisible = false;

				// calculate the approximate bounds of the toolbar "handle", based on the current toolbar bounds;
				// if these four points are within a single monitor then visible;
				// this function is not exact since toolbar might span monitors but close enough until we rearchitect -- at worst will just move toolbar to default position;
				// note the rightReference is calculated in relation to the left bounds
				const topReference = (currentBounds.top ?? 0) + 5;
				const bottomReference = (currentBounds.bottom ?? 0) - 5;
				const leftReference = currentBounds.left + 5;
				const rightReference = currentBounds.left + 20;

				for (let i = 0; i < monitorData.length; i++) {
					let topOK = false;
					let bottomOK = false;
					let leftOK = false;
					let rightOK = false;

					// since claimed space may change monitor dimensions, must compare against availableRect (which is independent of claimed space)
					if (topReference >= monitorData[i].monitor.scaledRect.top) topOK = true;
					if (bottomReference <= monitorData[i].monitor.scaledRect.bottom) bottomOK = true;
					if (leftReference >= monitorData[i].monitor.scaledRect.left) leftOK = true;
					if (rightReference <= monitorData[i].monitor.scaledRect.right) rightOK = true;

					isVisible = topOK && bottomOK && leftOK && rightOK;

					if (isVisible) break;
				}
				Logger.system.debug(
					"MONITOR: DockableWindow.isToolbarVisible",
					"monitorData",
					monitorData,
					"currentBounds",
					currentBounds,
					isVisible ? "isVisible" : "not visible"
				);
				Logger.system.debug(
					"MONITOR: DockableWindow.isToolbarVisible T-B-L-R=",
					topReference.toString(),
					bottomReference.toString(),
					leftReference.toString(),
					rightReference.toString()
				);
				resolve(isVisible);
			});
		};
		return new Promise(isToolbarVisiblePromiseResolver);
	}

	/**
	 * Returns a string representation of the docked location
	 */
	getDockedStringFromLocation(): string | null {
		let locValue: null | string = null;
		for (let i = 0; i < Object.keys(this.monitorDockablePositions).length; i++) {
			const key = Object.keys(this.monitorDockablePositions)[i];
			if (this.monitorDockablePositions[key] === this.dockedPosition) {
				locValue = key.toUpperCase();
				break;
			}
		}
		return locValue?.toLowerCase() ?? null;
	}

	/**
	 * @function getSharedEdges Determines if another window has edges to which our window may snap/dock
	 * @param  {type} win2 {description}		* @param  {Window} win2 Window proxy (movement object) of other window to which our window may snap/dock
	 * @return {type} {description}		* @param  {number} tolerance Buffer region around edges to determine region for snap / dock
	 * @return {object} Object describing shared edges, with boolean top, bottom, left, and right properties
	 */
	getSharedEdges(win2: DockableWindow, tolerance: number = 0): SharedEdges {
		if (tolerance === 0 && (this.win?.type === "external" || win2.win?.type === "external")) {
			tolerance = this.nativeEdgeTolerance;
		}
		return super.getSharedEdges(win2, tolerance);
	}

	/**
	 * Function to respond to finWindow bounds-changed events. Currently we only respond to this event in some cases.
	 * Aero_key: Sets bounds after an aero snap action is initiated (windows key + arrow key). Only triggers if Aero is enabled.
	 * System_restored: Triggered when a window is restored from minimized outside of Finsemble. This code block should only be hit if the
			monitor configuration has changed after a window is minimized and the window's old restore position would be partially off-monitor.
			The OS does not change the bounds of minimized windows, so they only action we can take	is to force it on monitor.
	 * @param {object} e The event
	 */
	onSystemMovedWindow(e: FinsembleEvent) {
		const incomingBounds = clone(e.data);
		let bounds;
		switch (e.data.action) {
			case MOVE_REASON.AERO_KEY:
				// If the user is moving the window (and not the OS) these movements will be handled elsewhere
				if (!this.win.finishedMove) return;
				Logger.system.log("aero_key: aero key action occurred", e.data.action);
				if (this.calculator.enableWindowsAeroSnap) {
					bounds = incomingBounds;
				} else {
					bounds = this.getBounds();
				}
				Logger.system.verbose(`aero_key: aero key action occurred. Action: ${e.data.action}, bounds: ${bounds}`);
				// Docking listens for a restoreFromMaximize message on the router.
				// That doesn't happen when aero-snap triggers a restore after the window is maximized.
				// Here we manually change the underlying window's windowState
				Logger.system.debug("setting window state back to NORMAL with win: ", this.win);
				this.win.windowState = WINDOWSTATE.NORMAL;
				// Update the Finsemble bounds after aero snap
				// For groups, break the group and and only aero snap the selected window.
				this.setBounds(bounds, () => {
					this.calculator.removeWindowFromAllGroups(this, true);
					this.calculator.buildSnapRelationships(this);
					this.calculator.updateGroupData();
				});
				break;
			// Triggered when a window is restored outside of finsemble (such as from the taskbar), and the previous window position would now be off-monitor
			// From Electron 9 the window restore position gets updated when a monitor change event occurs that would put it off monitor.
			// Finsemble's bounds need to be updated to match that position.
			// Groups also must be moved relative to one window in the group as the OS may break a group if it is partially off monitor or on a scaling change.
			case MOVE_REASON.SYSTEM_RESTORED: {
				const group = this.calculator.getMovableGroup(this.name);
				if (group) {
					Logger.system.verbose(
						`dockableWindow.onSystemMovedWindow system_restored: Window ${
							this.name
						} is in a group, updated bounds for restore ${JSON.stringify(incomingBounds)}`
					);
					if (group.groupState === group.GROUPSTATES.MINIMIZED) {
						group.restoreAll();
					}
					group.deferSystemMove({ name: this.name, bounds: incomingBounds }, this.calculator);
				} else {
					Logger.system.verbose(
						`dockableWindow.onSystemMovedWindow system_restored: Window ${
							this.name
						} is restoring to new position: ${JSON.stringify(incomingBounds)}`
					);
					this.setBounds(incomingBounds);
				}
				break;
			}
			default:
				break;
		}
	}

	/**
	 * Builds default docking options for dockable windows
	 * @param {object} options Taken from the incoming config
	 * @return {object} The docking defaults
	 */
	buildDefaultDockingOptions(options: DockingConfigOptions): DefaultDockingOptions {
		// Building a default options object based on the incoming config props. Since windows can have different dockable areas and different starting docked positions, we must build starting props based on those options.
		const hasUndockedParams =
			this.win.hasOwnProperty("dockingOptions") && this.win.dockingOptions.hasOwnProperty("undockedParams");
		const hasDockedParams =
			this.win.hasOwnProperty("dockingOptions") && this.win.dockingOptions.hasOwnProperty("dockedParams");
		return {
			isDockable: !!(
				options !== null &&
				(options.dockable === true || (Array.isArray(options.dockable) && Array.from(options.dockable).length > 0))
			),
			undockedWidth: hasUndockedParams ? this.win.dockingOptions.undockedParams.width : 300,
			undockedHeight: hasUndockedParams ? this.win.dockingOptions.undockedParams.height : 30,
			isDocked: !!(options !== null && options.hasOwnProperty("startsDocked")),
			dockedWidth: hasDockedParams ? this.win.dockingOptions.dockedParams.width : 300,
			dockedHeight: hasDockedParams ? this.win.dockingOptions.dockedParams.height : 39,
			height: 0,
		};
	}

	/**
	 * Function to handle chain of events when a dockable component type is docked
	 *
	 * @param {object} monitor Information about the monitor the component was docked to
	 * @param {string} location A string containing the location to dock to: "TOP", "LEFT", "BOTTOM", and "RIGHT" are the acceptable values
	 */
	dock(monitor: MonitorInfoDetail, oldBounds: DockingBounds, location: number) {
		if (this.isDockableComponent) {
			let dockPos: number | undefined = -1;
			this.undockedPosition = {
				width: oldBounds.width ?? 0,
				height: oldBounds.height ?? 0,
			};
			Logger.system.debug("MONITOR: DockableWindow.dock", "undockedPosition", this.undockedPosition);

			// Assuming the re-assignment of positions/size doesn't need to take place
			// we don't need a switch here
			switch (location) {
				case this.monitorDockablePositions.TOP:
					dockPos = this.monitorDockablePositions.TOP;
					break;
				case this.monitorDockablePositions.LEFT:
					dockPos = this.monitorDockablePositions.LEFT;
					break;
				case this.monitorDockablePositions.RIGHT:
					dockPos = this.monitorDockablePositions.RIGHT;
					break;
				case this.monitorDockablePositions.BOTTOM:
					dockPos = this.monitorDockablePositions.BOTTOM;
					break;
				default:
					break;
			}

			this.dockedPosition = dockPos;
			this.win.isDocked = true;
		}
	}

	/**
	 * Function to handle chain of events when a dockable component type is undocked
	 */
	async undock(isClosing: boolean = false) {
		if (this.isDockableComponent) {
			// Only change state if we are not closing
			if (!isClosing) {
				this.win.isDocked = false;
				this.dockedPosition = this.monitorDockablePositions.NONE;
			}
			await this.monitor.undockWindowFromMonitor(this, isClosing);
		}
	}

	saveDockableData() {
		const bounds = {
			top: this.top,
			bottom: this.bottom,
			left: this.left,
			right: this.right,
			width: this.width,
			height: this.height,
			isDockable: this.win.isDocked,
			dockedPosition: this.dockedPosition,
			monitor: this.monitor.rawMonitor.deviceId || null,
		};

		// this needs to be saved in here because something causes this to reverse in the callback below
		const visible = !this.isHidden;

		// must save the monitor date since if monitors changed since last restart then toolbar will use default settings (otherwise might have problems with visibility)
		System.getMonitorInfo((monitorInfo) => {
			const monitorData = [monitorInfo.primaryMonitor, ...monitorInfo.nonPrimaryMonitors];
			Logger.system.debug(
				"MONITOR: DockableWindow saveDockableData. Visible will always be saved as true.",
				monitorData,
				visible.toString(),
				bounds
			);

			// Always save true for visible because we don't want the toolbar to come back hidden
			// Save if the dashbar is enabled as the bounds are based on that state. If the config changes this state on a later boot
			// we need to know if those bounds should be updated to match the toolbar's state in config.
			StorageClient.save({
				topic: this.name,
				key: this.name,
				value: {
					visible,
					"window-bounds": bounds,
					monitorData,
					dashbarEnabled: this.hasDashbarData,
					alwaysOnTop: this.isAlwaysOnTop(),
					dockedPositionBeforeHidden: this.dockedPositionBeforeHidden,
				},
			});
		});
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	onBoundsChanged() {
		Logger.system.debug("MONITOR: onBoundsChanged", this.isDockableComponent);
		this.resizeHandle = null;
		const timestamp = Date.now();
		// This would be true if the window was maximized when the move request that triggered mouseDown was requested.
		// It should only be changed if, on mouse down the window is maximized.
		this.shouldOffsetByMouse = false;
		this.onboundschanged = timestamp;
		this.lastBoundsAdjustment = timestamp;
		this.monitor = this.calculator.getMonitorForWindow(this);
		this.calculator.onMouseUp();
		this.win._stopMove();

		if (this.isDockableComponent || this.win.componentType == "Toolbar") {
			Logger.system.debug("MONITOR: onBoundsChanged saveDockableData");
			this.saveDockableData();
		}
	}

	async hiddenEventHandler() {
		const isWin32 = await isWindows();
		// this only makes sense if we're on Window OS - for OSX we can skip the entirety
		// of this handler - check the comment below for more details
		if (isWin32 && this.win.isDocked && !this.isHidden) {
			this.monitor.undockWindowFromMonitor(this);
			// save the docked position before hiding
			this.dockedPositionBeforeHidden = this.dockedPosition ?? null;
			this.isHidden = true;
			this.win.isDocked = false;

			// Whenever we hide toolbar we want to placed it in an innocuous position
			// outside the docking area and with its undocked dimensions
			// if someone invoked finsembleWindow.show it will allows them to manually drag the toolbar
			// to its docking area with no problems
			// the other instances of the Toolbar state are only Dock (Ctrl + alt + shift + t) and
			// show at mouse position (Ctrl + alt + t) for which is irrelevant where the toolbar is placed when hidden
			if (this.win.componentType === "Toolbar") {
				this.setBounds({
					top: 100,
					left: 100,
					width: this.win.dockingOptions.undockedParams.width,
					height: this.win.dockingOptions.undockedParams.height,
					right: 0,
				});
			}
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	shownEventHandler(event) {
		// Tiago Pina - 2023/02/06
		// code was removed from this event handler as it was creating undesired side effects
		// all invocations to docking are done previously and whenever this event is fired the window
		// should already have its correct state (docked/undocked/shown) - This also removed the flashing
		// and the jumping up and down of the window when manually docking

		// we need to update this instance's isHidden property to keep it synced with the status of the window.
		// An edge-case is when autoShow config is set to false and we invoke finsembleWindow.show we need to update
		// the isHidden flag to reflect the state change. Otherwise, we were having some nasty side effects.
		// For example: KB52417 tabbing flashing issues - when this instance is accessed in dockingMain
		// for example to getMouseInWindows - if this flag states that the window is hidden then it will skip the necessary iteration
		// since it would assume that the window was still hidden
		this.isHidden = false;

		// resets the dockedPositionBeforeHidden when the window is shown
		this.dockedPositionBeforeHidden = null;
	}

	/**
	 * @function {function name}
	 * @param  {type} request  {description}
	 * @param  {type} callback {description}
	 * @return {type} {description}
	 */
	async onBoundsChanging(event: DockingSimulatedEvent | FinsembleEvent, cb?: () => void): Promise<void> {
		// If bounds are changing then we can't be maximized. This can occur when a maximized window is moved by API or system event, such as a change in monitor size
		this.win.windowState = WINDOWSTATE.NORMAL;
		this.win.removeEventListener("bounds-change-request", this.onBoundsChanging);
		const request = event.data || event;
		request.name = event.source;
		this.win._startMove(request.changeType);
		try {
			request.timestamp = Date.now();
		} catch (e) {
			return;
		}
		const position = await System.getMousePosition();
		const finishMove = () => {
			this.lastBoundsAdjustment = Date.now();
			this.win.addEventListener("bounds-change-request", this.onBoundsChanging);
		};
		let invalidateRequest = false;
		const shouldThrottle = request.timestamp < this.lastBoundsAdjustment + this.resizeThrottlePeriod;

		// Note: this conditional here is for testing. Node is faster than JS or something.
		// In node, we need to also exclude requests that came in at the same time as the request we last processed.
		// I think they sometimes send multiple events and in my testing, that never happens.
		// Truthfully though I don't know why this bit has to be different in the different environments.
		// But I do remember losing a day when I ported this stuff from test-world. Don't delete it. --Brad
		// @ts-ignore fin object referenced for testing env
		if (typeof fin === "undefined") {
			invalidateRequest = shouldThrottle || request.timestamp < this.lastBoundsAdjustment;
		} else {
			invalidateRequest = shouldThrottle || request.timestamp <= this.lastBoundsAdjustment;
		}

		if (invalidateRequest) {
			Logger.system.debug(
				"DOCKING: Invalid request",
				"TIMESTAMPS:",
				JSON.stringify(request.timestamp),
				JSON.stringify(this.lastBoundsAdjustment)
			);
			this.win.addEventListener("bounds-change-request", this.onBoundsChanging);
			cb?.();
			return;
		}

		// Commented out by Terry during tabbing refactor
		//if (this.win.windowState === WINDOWSTATE.MAXIMIZED) {
		/**
		 * This is a way to lock out any processing until we finish processing the first move request
		 * that comes in when a window is maximized.
		 *
		 * Events more or less get 'queued' by the underlying container. As quickly as they can, our container
		 * will consume OS-level events and bubble them up to this layer. We have code in this file
		 * here that says "Hey, this request came in prior to the last time I made a change. I'm going to drop
		 * this request . You're invalid. Goodbye." The thing we trigger off of is `lastBoundsAdjustment`.
		 * This value is set _after_ we process the current move request.
		 *
		 * Maximize is special though. As soon as our first request comes in, we want to ensure that no additional requests are even considered.
		 * To do that, we just say "The last time we changed this window was super far into the future." Now,
		 * every incoming request will be dropped until we have time to restore the window, do any group adjustments, and recalibrate.
		 *
		 * It sounds clunky, and it is somewhat. But I can't think of a better way to do this.
		 */
		//this.lastBoundsAdjustment = Date.now() + 1000000000;

		/**
		 * This tells the `setMoveRequest` function to offset the incoming request in such a way
		 * that the window is centered on the mouse pointer. If you call setBounds on a window that is maximized, subsequent move
		 * requests will come in as though the window's left edge never moved, until mouseUp happens. This boolean helps us get
		 * around this bug.
		 */
		//this.shouldOffsetByMouse = true;
		//}

		// This comparison used to check right and bottom...props that don't exist on the request.
		// If all of the bounds haven't changed, don't waste time processing it.
		// On monitor resolution change, bounds will not change, but we still need to move
		// If the changeSource is "system", we might need to move even if the bounds haven't changed.
		if (
			request.changeSource !== "system" &&
			request.left === this.win.windowOptions.left &&
			request.top === this.win.windowOptions.top &&
			request.width === this.win.windowOptions.width &&
			request.height === this.win.windowOptions.height
		) {
			console.debug("No change being requested. Dropping.");
			finishMove();
			cb?.();
			return;
		}

		// An electron bug causes window size to fluctuate on display scaled monitors. Keep the size if we are moving.
		if (request.changeType === 0) {
			request.width = this.win.windowOptions.width ? this.win.windowOptions.width : request.width; // For native windows these values do not exist initially
			request.height = this.win.windowOptions.height ? this.win.windowOptions.height : request.height;
		}

		request.right = request.left + request.width;
		request.bottom = request.top + request.height;
		request.groupNames = this.groupNames;
		request.mousePosition = request.mousePosition || position;

		const bounds = await this.calculator.requestMove(request);
		if (!bounds) {
			finishMove();
			cb?.();
			return;
		}

		await this.setBounds(bounds);
		finishMove();
		cb?.();
	}

	setResizeThrottlePeriod(throttlePeriod: number) {
		this.resizeThrottlePeriod = throttlePeriod;
	}

	async restored(event: FinsembleEvent) {
		const group = this.calculator.getMovableGroup(this.name);
		if (group && group.groupState === group.GROUPSTATES.MINIMIZED) {
			group.restoreAll();
		}
		let previousState = event?.data?.previousState;
		let updatedBounds = event?.data?.bounds;
		if (!updatedBounds) return;
		/* If the window has been restored for maximize we need to update it's new position for Finsemble
		We mostly rely on electron restore; however, in some cases we've forced the restore to a saved position.
		If the monitors have changed this old position may be off monitor so we need to handle this case

		Group restore needs more work. Historically we've always assumed the maximized window in the group will end up properly
		positioned in the group. This doesn't work out after monitor change - the restore position that comes back from electron is not matched with the group
		For now we make sure the restored window isn't lost, but can't guarantee the group will end up together (which will cause it to break after the next user move). */
		let adjustedBounds: DockingBounds = { ...(await adjustBoundsToBeOnMonitorAsync(updatedBounds)) };
		if (previousState === WINDOWSTATE.MAXIMIZED && !checkIfBoundsAreEqual(this.getBounds(), adjustedBounds)) {
			// If the bounds from the event are not off-monitor, it's safe to only update the bounds for finsemble and not do a full setbounds
			if (checkIfBoundsAreEqual(adjustedBounds, updatedBounds)) {
				adjustedBounds.internal = true;
			}
			await this.setBounds(adjustedBounds);
		}

		if (!group && previousState && !this.name.includes("Toolbar")) {
			this.calculator.buildSnapRelationships(this);
			this.calculator.formGroup(this.name, { isMovable: false });
			this.calculator.updateGroupData();
		}
	}

	maximized() {
		// Monitor bounds are used to update finsemble bounds because of a chromium bug where maximized bounds are off by several pixels.
		// This varies by scaling but on 100% monitor on a primary bounds were -8,-8 instead of 0,0 and 16 pixels too too and wide.
		let bounds = { ...this.monitor.availableRect };
		// We need the window service's internal state to reflect the maximized bounds so that other windows don't try to
		// snap to the old location. But, we must not ask Electron to actually set new bounds because that will then erase
		// Electron's restoration cache (getNormalBounds). This call will update Finsemble's internal state without making any
		// actual calls to the window itself.
		//One side effect of this change is that FEA's bounds no longer get updated by us here.
		// Electron has a strange issue where maximized bounds have an invisible border or some
		// sorts that doesn't cause any issues but shows up in the bounds
		// (On my 100% machine I get x: -8, y: -8 width and height have an extra 16 pixels).
		// By not updating this value FEAs bounds now match electron bounds here.
		// This may not matter but we do rely on FEA bounds in several places in mainWindow
		// It's possible there will be some side effects.
		this.setInternalBounds(bounds);
	}

	/**
	 * This removes event listeners.
	 * I can't be entirely certain, but from my testing, it _appears_ that Electron isn't actually deleting these objects.
	 * If you close the window, then load the window with the same name, old listeners are still registered with your new window.
	 * So if you reload a workspace, and then try to move a window, you get weird scenarios where onBoundsChanging is called twice with different values (presumably for old eventlisteners).
	 * Removing the eventListeners on close seems to handle this.
	 */
	removeEventListeners() {
		// This reason this is necessary is hard to explain.
		// When the dockableWindow is created we call win.addEventListener("bounds-change-request", blah blah).
		// This forces the window to bubble bounds events up to us. This function also goes out to the router and is pretty heavy for rapid addition/removal. Inside of onBoundsChanging, we add and remove handlers _on the event manager_. So when removeEventListeners is called, the two handlers above are removed. However, there's still that lingering listener that we added to the event manager inside of finishMove. If we don't remove that listener, we could get events thrown from other older incarnations of the same dockable window.
		this.win.removeEventListener("bounds-change-request", this.onBoundsChanging);
		this.win.removeEventListener("bounds-changed", this.onBoundsChanged);
		this.win.removeEventListener("hidden", this.hiddenEventHandler);
		this.win.removeEventListener("shown", this.shownEventHandler);
		this.win.removeEventListener("restored", this.restored);
		this.win.removeEventListener("maximized", this.maximized);
		this.win.removeEventListener("system-bounds-changed", this.onSystemMovedWindow);
	}

	/** ******************************************
	 *											*
	 *			Window Moving Methods			*
	 *											*
	 ******************************************* */

	/**
	 * Hides taskbar icon for windows. This prevents them from being clobbered by aeroshake.
	 */
	hideTaskbarIcon() {
		const hideTaskbarPromise = new Promise<void>((resolve) => {
			// Check to see if the method exists. It wouldn't on an external window wrapper.
			if (this.win._updateOptions) {
				this.win._updateOptions({ showTaskbarIcon: false }, resolve);
			} else {
				resolve();
			}
		});

		// The timeout should never fire. Showing/hiding the taskbar should take
		// a couple dozen milliseconds
		// However, updateOptions also saves data. If we ever lost a connection to the DB,
		// we could get into a situation where the queue hangs up. We don't want that.
		this.changeTaskbarVisibilityQueue(() => wrapWithTimeout(hideTaskbarPromise, 2000, "HideTaskbarIcon timed out"));
	}

	/**
	 * This shows the taskbar icon for a given window.
	 */
	showTaskbarIcon(showIcon: boolean = this.shouldShowTaskbarIcon) {
		// Don't try to set the taskbar icon if the window is closing
		if (["closing", "closed"].includes(this.win.wrapState)) return;

		const showTaskbarPromise = new Promise<void>((resolve) => {
			if (this.win._updateOptions && (this.shouldShowTaskbarIcon || showIcon)) {
				this.win._updateOptions({ showTaskbarIcon: true }, resolve);
			} else {
				resolve();
			}
		});
		// The timeout should never fire. Showing/hiding the taskbar should take
		// a couple dozen milliseconds
		// However, updateOptions also saves data. If we ever lost a connection to the DB,
		// we could get into a situation where the queue hangs up. We don't want that.
		this.changeTaskbarVisibilityQueue(() => wrapWithTimeout(showTaskbarPromise, 2000, "ShowTaskbarIcon timed out"));
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	disableFrame() {
		if (this.win.disableFrame) {
			// This should be renamed disableWindowMovement();
			this.win.disableFrame();
		}
	}

	async setOpacity(
		params: { opacity: number; persist?: boolean } = { opacity: 1, persist: false }
	): StandardPromise<void> {
		//handle WebWindowWrapper vs. FinsembleWindow
		if (this.win._setOpacity) {
			this.win._setOpacity(params);
		} else {
			this.win.setOpacity(params);
		}

		return { err: null };
	}

	/**
	 * Sets bounds for internal calculations.
	 */
	setInternalBounds(bounds: DockingBounds) {
		// Bunch of if statements was quicker than ternary operators when inside of loops.
		this.left = typeof bounds.left === "undefined" ? this.left : bounds.left;
		this.top = typeof bounds.top === "undefined" ? this.top : bounds.top;
		this.width = typeof bounds.width === "undefined" ? this.width : bounds.width;
		this.height = typeof bounds.height === "undefined" ? this.height : bounds.height;
		this.right = typeof bounds.right === "undefined" ? this.left + this.width : bounds.right;
		this.bottom = typeof bounds.bottom === "undefined" ? this.top + this.height : bounds.bottom;
		this.setBoundingBoxes();
	}

	/**
	 * @function {function name}
	 * @param  {type} bounds    {description}
	 * @param  {type} successCB {description}
	 * @param  {type} errCB     {description}
	 * @return {type} {description}
	 */
	async setBounds(bounds: DockingBounds, successCB?: StandardCallback): StandardPromise<void> {
		return new Promise((resolve) => {
			const newMonitor = this.calculator.getMonitorForWindow(this);

			// indicates the moving window is split between two monitors
			const crossingMonitor = () => {
				if (this.monitor.name != newMonitor.name) return true;

				if (
					bounds.right > newMonitor.availableRect.right ||
					bounds.left < newMonitor.availableRect.left ||
					(newMonitor.availableRect.top !== undefined &&
						bounds.top !== undefined &&
						bounds.top < newMonitor.availableRect.top) ||
					(newMonitor.availableRect.bottom !== undefined &&
						bounds.bottom !== undefined &&
						bounds.bottom > newMonitor.availableRect.bottom)
				) {
					return true;
				}
			};

			// While moving native window across 2 monitors with different scale the .net client sends very big values of new window size.
			// It happens because OS re-renders the window with bounds which Finsemble can not manage.
			// In that case just ignore new bounds while the window will not be fully rendered on the one of monitors
			if (bounds.notUpdateAssimilation && crossingMonitor()) {
				successCB?.(null, null);
				resolve({ err: null, data: undefined });
			}

			this.setInternalBounds(bounds);
			bounds = {
				left: bounds.left,
				width: bounds.width,
				height: bounds.height,
				top: bounds.top,
				right: bounds.right,
				bottom: bounds.bottom,
				internal: bounds.internal,
				persistBounds: bounds.persistBounds,
				notUpdateAssimilation: bounds.notUpdateAssimilation,
			};
			// Old way was to update the monitor on the window when onMouseUp occurred, but now the OS (as well as user) can move a window and the monitor for that window should be updated every time
			this.monitor = newMonitor;
			this.win._setBounds({ bounds }, (err, data) => {
				this.calculator.buildSnapRelationships(this);
				// Update the monitor stored in options. This is the only value that tracks a window's monitor in the workspace and needs to be up to date when spawning by percentage.
				this.win._updateOptions({ monitorInfo: this.monitor?.rawMonitor.position ?? 0 });
				successCB?.(err, data);
				resolve({ err, data });
			});
		});
	}

	/**
	 * @function {function name}
	 * @param  {type} left {description}
	 * @param  {type} top  {description}
	 * @return {type} {description}
	 */
	moveTo(left: number, top: number) {
		this.setBounds({
			left,
			top,
			right: left + this.win.windowOptions.width,
			bottom: top + this.win.windowOptions.height,
			width: this.win.windowOptions.width,
			height: this.win.windowOptions.height,
		});
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	getGroupNames(): string[] {
		return this.groupNames;
	}

	/**
	 * @function {function name}
	 * @param  {type} request  {description}
	 * @param  {type} callback {description}
	 * @return {type} {description}
	 */
	requestBoundsChange(request: MoveRequest, callback: () => void) {
		// window.methodCalls["requestBoundsChange"]++;
		// for testing purposes only
		if (!callback) {
			callback = this.onBoundsChanged;
		}
		System.getMousePosition((err, position) => {
			this.onBoundsChanging(
				{
					data: {
						corners: this.getCornerObject(request),
						left: request.left,
						top: request.top,
						width: request.width,
						height: request.height,
						changeType: request.changeType,
						name: this.name,
						mousePosition: request.mousePosition || position,
					},
				},
				() => {
					if (callback) {
						callback();
					}
				}
			);
		});
	}

	/** ******************************************
	 *											*
	 *			Helper Functions				*
	 *											*
	 ******************************************* */

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	calculateInnerBoundingBox() {
		const adjustment = (this.bufferSize ?? 0) * 2;
		return {
			min: {
				x: this.left + adjustment,
				y: this.top + adjustment,
			},
			max: {
				x: this.right - adjustment,
				y: this.bottom - adjustment,
			},
		};
	}

	getInnerBoundingBox() {
		return this.innerBuffer;
	}

	/**
	 * @function {function name}
	 * @param  {type} bufferSize {description}
	 * @return {type} {description}
	 */
	setBufferSize(bufferSize: number) {
		this.bufferSize = bufferSize;
		this.setBoundingBoxes();
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	setBoundingBoxes() {
		const myBounds = this.getBounds();
		this.setBuffer();
		this.innerBuffer = this.calculateInnerBoundingBox();
		this.windowBoundingBox = BoxMath.getWindowBoundingBox(myBounds);
		this.setSnappingRegions();
		this.vertices = this.getCornerObject(myBounds);
	}

	getSnappingRegions() {
		return this.snappingRegions;
	}

	updateState() {
		this.win._getBounds({}, (err, bounds) => {
			this.setBounds(bounds);
		});
	}

	/**
	 * @function setHidden
	 * @param  {function=} callback when done
	 *
	 * Sets the internal state to hidden.  Only needed if window is hidden outside of this class.
	 */
	setHidden() {
		Logger.system.debug("MONITOR: DockableWindow hide state");
		this.isHidden = true;
	}

	/**
	 * @function setHidden
	 * @param  {function=} callback when done
	 *
	 * Sets the internal state to shown (i.e. not hidden). Only needed if window is hidden outside of this class.
	 */
	setShown() {
		Logger.system.debug("MONITOR: DockableWindow.show state");
		this.isHidden = false;
	}

	hide(cb: StandardCallback = NullStandardCallback) {
		this.win._hide({}, cb);
	}

	show(cb: StandardCallback = NullStandardCallback) {
		this.win._show({}, cb);
	}

	maximize(cb: StandardCallback = NullStandardCallback) {
		this.win._maximize({}, cb);
	}

	minimize(cb: StandardCallback = NullStandardCallback) {
		this.win._minimize({}, cb);
	}

	restore(cb: StandardCallback = NullStandardCallback) {
		this.win._restore({}, cb);
	}

	alwaysOnTop(isAlwaysOnTop: boolean) {
		try {
			this.win._alwaysOnTop({ alwaysOnTop: isAlwaysOnTop });
		} catch (e) {
			Logger.system.debug("Implement alwaysOnTop");
		}
	}

	/**
	 * Returns the present alwaysOnTop status of the window.
	 *
	 * NOTE: This only reflects Finsemble's internal representation
	 * of the alwaysOnTop status, not the container/OS. When changing the status,
	 * Finsemble's status changes before the underlying container, causing them
	 * to briefly become out of sync.
	 */
	isAlwaysOnTop(): boolean {
		return this.win._isAlwaysOnTop();
	}

	bringToFront() {
		try {
			this.win._bringToFront({});
		} catch (e) {
			Logger.system.error("Implement bringToFront");
		}
	}

	notifyWindowTitleBarOfGroupMembership() {
		if (typeof RouterClient !== "undefined") {
			RouterClient.transmit(`${this.name}.groupMembershipChange`, {
				type: "joined",
			});
		}
	}

	notifyWindowTitleBarOfGroupEjection() {
		if (typeof RouterClient !== "undefined") {
			RouterClient.transmit(`${this.name}.groupMembershipChange`, {
				type: "ejected",
			});
		}
	}

	addSnappedWindow(snapObj: SnappedObject) {
		if (!this.snappedWindows) {
			this.snappedWindows = [];
		}
		let shouldAdd = true;
		for (let i = 0; i < this.snappedWindows.length; i++) {
			const snappedWin = this.snappedWindows[i];
			if (snappedWin.name === snapObj.name) {
				shouldAdd = false;
				break;
			}
		}

		if (shouldAdd) {
			this.snappedWindows.push(snapObj);
		}
	}

	removeSnappedWindow(name: string) {
		if (this.snappedWindows) {
			for (let i = 0; i < this.snappedWindows.length; i++) {
				const snappedWin = this.snappedWindows[i];
				if (name === snappedWin.name) {
					// Logger.system.log("Removing", name, "from", this.name);
					this.snappedWindows.splice(i, 1);
					return;
				}
			}
		}
	}

	animatePositionAndHeight(params: DockingBounds, cb: StandardCallback) {
		this.setInternalBounds(params);
		if (this.win._setBounds) {
			params.persistBounds = true;
			this.win._setBounds({ bounds: params }, cb);
		}
	}

	isGrouped(): boolean {
		return this.groups.length > 0;
	}

	/**
	 * Adds eventListeners so that when the finWIndow moves, we can do things with that data.
	 */
	addListeners() {
		this.win.addEventListener("bounds-change-request", this.onBoundsChanging);
		this.win.addEventListener("bounds-changed", this.onBoundsChanged);
		this.win.addEventListener("hidden", this.hiddenEventHandler);
		this.win.addEventListener("shown", this.shownEventHandler);
		this.win.addEventListener("restored", this.restored);
		this.win.addEventListener("maximized", this.maximized);
		this.win.addEventListener("system-bounds-changed", this.onSystemMovedWindow);
	}
}
export default DockableWindow;
